// Import zstd decompression
import { decompress } from 'fzstd'
import type { EncryptionKeys } from '@/types/encryption'
import { makeBigIntSafe } from '@/utils/bigintSafe'

// Helper function to convert base64 to Uint8Array
function base64ToUint8Array(base64: string): Uint8Array {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

// Helper function to convert Uint8Array to base64
function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary)
}

// Helper function to concatenate Uint8Arrays
function concatenateUint8Arrays(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((acc, arr) => acc + arr.length, 0)
  const result = new Uint8Array(totalLength)
  let offset = 0
  for (const arr of arrays) {
    result.set(arr, offset)
    offset += arr.length
  }
  return result
}


/**
 * Derives encryption and MAC keys from the base key.
 * Must match the agent's key derivation.
 */
export async function deriveKeys(keyBase64: string): Promise<EncryptionKeys> {
  const keyBytes = base64ToUint8Array(keyBase64)
  
  // Create encoder for prefixes
  const encoder = new TextEncoder()
  
  // Derive encryption key using SHA-256
  const encKeyData = concatenateUint8Arrays(encoder.encode('ENC:'), keyBytes)
  const encKeyHash = await crypto.subtle.digest('SHA-256', encKeyData)
  const encKey = await crypto.subtle.importKey(
    'raw',
    encKeyHash,
    { name: 'AES-CBC' },
    false,
    ['encrypt', 'decrypt']
  )
  
  // Derive MAC key using SHA-256
  const macKeyData = concatenateUint8Arrays(encoder.encode('MAC:'), keyBytes)
  const macKeyHash = await crypto.subtle.digest('SHA-256', macKeyData)
  const macKey = new Uint8Array(macKeyHash)
  
  return { encKey, macKey }
}

// Compute HMAC-SHA256
async function computeHMAC(key: Uint8Array, data: Uint8Array): Promise<Uint8Array> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    key,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )
  const signature = await crypto.subtle.sign('HMAC', cryptoKey, data)
  return new Uint8Array(signature)
}

// Verify HMAC
async function verifyHMAC(key: Uint8Array, data: Uint8Array, mac: Uint8Array): Promise<boolean> {
  const expectedMac = await computeHMAC(key, data)
  if (mac.length !== expectedMac.length) return false
  
  // Constant-time comparison
  let result = 0
  for (const [index, macByte] of mac.entries()) {
    result |= macByte ^ (expectedMac[index] ?? 0)
  }
  return result === 0
}

export async function encryptMessage(message: string, keyBase64: string): Promise<string> {
  const { encKey, macKey } = await deriveKeys(keyBase64)
  
  // Generate random IV
  const iv = crypto.getRandomValues(new Uint8Array(16))
  
  // Encode message - let Web Crypto API handle padding automatically
  const encoder = new TextEncoder()
  const messageBytes = encoder.encode(message)
  
  // Use Web Crypto API's built-in PKCS7 padding by NOT specifying padding
  // AES-CBC automatically uses PKCS7 padding in Web Crypto API
  // Let Web Crypto handle padding
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    encKey,
    messageBytes
  )
  const ciphertextBytes = new Uint8Array(ciphertext)
  
  // Compute HMAC over IV + ciphertext
  const dataToMac = concatenateUint8Arrays(iv, ciphertextBytes)
  const mac = await computeHMAC(macKey, dataToMac)
  
  // Concatenate IV + ciphertext + MAC and return base64
  const result = concatenateUint8Arrays(iv, ciphertextBytes, mac)
  return uint8ArrayToBase64(result)
}

/**
 * Decrypts AES-256-CBC encrypted data with HMAC verification.
 * Returns raw bytes for format detection.
 */
export async function decryptBytes(encrypted: string, keyBase64: string): Promise<Uint8Array> {
  const { encKey, macKey } = await deriveKeys(keyBase64)

  const encryptedData = base64ToUint8Array(encrypted)

  // Check minimum length (16 bytes IV + 16 bytes min ciphertext + 32 bytes MAC)
  if (encryptedData.length < 64) {
    throw new Error('Invalid encrypted data length')
  }

  // Extract IV, ciphertext, and MAC
  const iv = encryptedData.slice(0, 16)
  const mac = encryptedData.slice(-32)
  const ciphertext = encryptedData.slice(16, -32)

  // Verify HMAC first (before decryption)
  const dataToVerify = concatenateUint8Arrays(iv, ciphertext)
  const isValid = await verifyHMAC(macKey, dataToVerify, mac)
  if (!isValid) {
    throw new Error('HMAC verification failed - data may have been tampered with')
  }

  // Decrypt - Web Crypto API automatically handles PKCS7 padding removal
  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-CBC', iv },
    encKey,
    ciphertext
  )

  return new Uint8Array(decrypted)
}

export async function encryptData(data: Record<string, unknown>, keyBase64: string): Promise<string> {
  // Generate a unique nonce (max 64 chars as per server requirements) 
  // Try a simpler format that matches server expectation
  const nonce = `unique-request-id-${Math.floor(Date.now() / 1000)}`
  
  // Create the exact structure the server expects - nonce and timestamp go inside data field
  // Unix timestamp as float (like Python time.time())
  // Spread the original data fields
  const payload = {
    type: data.type,
    timeout: data.timeout,
    data: {
      nonce,
      timestamp: Date.now() / 1000,
      ...(typeof data.data === 'object' && data.data !== null ? data.data : {})
    }
  }
  
  // Create JSON string with no extra whitespace and validate it
  const message = JSON.stringify(payload)
  
  // JSON structure is now correct - nonce and timestamp are in data field
  
  // Defensive check: ensure JSON is valid and clean
  // Validate the JSON
  try {
    JSON.parse(message)
  } catch (error) {
    throw new Error(`Invalid JSON generated for encryption: ${String(error)}`, { cause: error })
  }
  
  // Check for non-printable characters that might cause parsing issues
  for (let i = 0; i < message.length; i++) {
    const charCode = message.charCodeAt(i)
    if (charCode < 32 && charCode !== 9 && charCode !== 10 && charCode !== 13) {
      throw new Error(`Invalid character at position ${i}: charCode=${charCode}`)
    }
  }
  
  return encryptMessage(message, keyBase64)
}

// Helper function to handle JSON parsing with metadata removal
function handleJsonParsing(data: string): Record<string, unknown> | string {
  // Check if the decrypted message contains our metadata fields
  try {
    // Apply BigInt-safe conversion before parsing to prevent precision loss
    // on large INT64 values (e.g., Milvus auto-generated IDs).
    const parsed: unknown = JSON.parse(makeBigIntSafe(data))

    // Only remove timestamp and nonce if they exist (for request decryption)
    if (typeof parsed === 'object' && parsed !== null && 'timestamp' in (parsed as { timestamp?: unknown }) && 'nonce' in (parsed as { nonce?: unknown })) {
      const { timestamp: _timestamp, nonce: _nonce, ...dataWithoutMeta } = parsed as { timestamp?: unknown; nonce?: unknown; [k: string]: unknown }
      // If there are no other fields, return the raw decrypted string
      if (Object.keys(dataWithoutMeta).length === 0) {
        return data
      }
      return dataWithoutMeta
    }

    // If no metadata fields, return parsed data
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>
    }
    return data
  } catch {
    // If not valid JSON, return the raw decrypted string
    return data
  }
}

/**
 * Decrypts an agent response.
 *
 * Format: Agent Response -> ZSTD Compress -> AES Encrypt -> Base64
 */
export async function decryptData(encrypted: string, keyBase64: string): Promise<Record<string, unknown> | string> {
  // Step 1: AES decrypt with HMAC verification (returns raw bytes)
  const decryptedBytes = await decryptBytes(encrypted, keyBase64)

  // Step 2: ZSTD decompress
  const decompressed = decompress(decryptedBytes)

  // Step 3: Parse JSON
  const jsonStr = new TextDecoder().decode(decompressed)
  return handleJsonParsing(jsonStr)
}